import { useEffect, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { MonthCalendar } from "./MonthCalendar";

const fmt = (iso, opts) => new Date(`${iso}T00:00`).toLocaleDateString("es-AR", opts);
const shift = (iso, n) => { const x = new Date(`${iso}T12:00`); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const todayAR = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" });

const Arrow = ({ dir, onClick, disabled, testid }) => {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-label={dir < 0 ? "Días anteriores" : "Días siguientes"}
      className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--forest)] text-white shadow-md transition-[transform,background-color,opacity] duration-200 hover:scale-105 hover:bg-[var(--forest-2)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 sm:h-14 sm:w-14">
      <Icon className="h-7 w-7" strokeWidth={2.75} />
    </button>
  );
};

const Day = ({ x, active, onPick, testid }) => (
  <button type="button" disabled={!x.trips} onClick={() => onPick(x.date)} data-testid={testid}
    className={`min-w-[5.5rem] flex-1 rounded-2xl px-3 py-2.5 sm:min-w-[6.5rem] text-left transition-colors duration-200 ${active ? "bg-[var(--forest)] text-white" : x.trips ? "bg-white ring-1 ring-[var(--forest)]/30 hover:bg-[var(--lime)]/40" : "cursor-not-allowed bg-black/[0.04] text-black/40"}`}>
    <span className="block font-mono text-[11px] uppercase tracking-[0.12em]">{fmt(x.date, { weekday: "short" })}</span>
    <span className="block font-semibold">{fmt(x.date, { day: "numeric", month: "short" })}</span>
    <span className={`block text-xs ${active ? "text-[var(--lime)]" : x.trips ? "text-[var(--forest)]" : ""}`}>{x.trips ? `desde ${formatARS(x.price)}` : "Sin salida"}</span>
  </button>
);

/** Días con salida alrededor de la fecha buscada (2 antes, el día y 2 después). */
export const DayStrip = ({ o, d, f, onPick, testid }) => {
  const [cal, setCal] = useState(null);
  const [center, setCenter] = useState(f);
  const [month, setMonthOpen] = useState(false);
  useEffect(() => { setCenter(f); }, [o, d, f]);
  useEffect(() => {
    let alive = true;
    api.get("/trips/calendar", { params: { origin: o, destination: d, date: center } })
      .then((r) => { if (alive) setCal({ days: Array.isArray(r.data?.days) ? r.data.days : [], next: r.data?.next ?? null, center }); })
      .catch(() => { if (alive) setCal(null); });
    return () => { alive = false; };
  }, [o, d, center]);
  if (!cal) return null;
  const home = cal.center === f;
  const today = cal.days.find((x) => x.date === f);
  const none = home && !today?.trips;
  const others = cal.days.some((x) => x.trips && x.date !== f);
  const atStart = shift(center, -2) <= todayAR();
  return (
    <div data-testid={testid} className={`rounded-3xl p-4 ${none ? "bg-[var(--lime)]/25 ring-1 ring-[var(--forest)]/20" : ""}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <CalendarDays className="h-4 w-4 shrink-0 text-[var(--forest)]" />
        {none ? (others || cal.next ? `El ${fmt(f, { weekday: "long", day: "numeric" })} no pasa el servicio de ${o} a ${d}. Elegí un día con salida:` : `No hay salidas de ${o} a ${d} en los próximos días.`) : `Salidas de ${o} a ${d} cerca de esa fecha`}
      </p>
        <button type="button" onClick={() => setMonthOpen(true)} data-testid={`${testid}-month-button`}
          className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--forest)] px-4 py-2 text-sm font-bold text-[var(--forest)] transition-colors duration-200 hover:bg-[var(--forest)] hover:text-white">
          <CalendarDays className="h-4 w-4" /> Ver mes
        </button>
      </div>
      <MonthCalendar o={o} d={d} f={f} open={month} onClose={() => setMonthOpen(false)} onPick={onPick} testid={`${testid}-month`} />
      <div className="flex items-center gap-2">
        <Arrow dir={-1} disabled={atStart} onClick={() => setCenter(shift(center, -5) < shift(todayAR(), 2) ? shift(todayAR(), 2) : shift(center, -5))} testid={`${testid}-prev-days`} />
        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1">
          {cal.days.map((x) => <Day key={x.date} x={x} active={x.date === f} onPick={onPick} testid={`${testid}-day-${x.date}`} />)}
        </div>
        <Arrow dir={1} onClick={() => setCenter(shift(center, 5))} testid={`${testid}-next-days`} />
      </div>
      {cal.next && home && (
        <button type="button" onClick={() => onPick(cal.next.date)} data-testid={`${testid}-next`} className="mt-3 rounded-full bg-[var(--forest)] px-5 py-2.5 text-sm font-bold text-white">
          Próxima salida: {fmt(cal.next.date, { weekday: "long", day: "numeric", month: "long" })} · desde {formatARS(cal.next.price)}
        </button>
      )}
    </div>
  );
};
